/**
 * Appipa Variant Integration
 * Handles the integration between variant picker and Appipa calculator
 * Works with auto-rendered calculator from ProcessProductPage
 */

// Get calculator manager functions from global scope or wait for them
let hideCalculator, showCalculator, hideCalculatorAndRestoreDefaults, isCalculatorRendered, waitForAndHideCalculator;

/**
 * Hides inputs inside variant-option fieldsets that have values starting with "Default_cpc"
 * This is used to hide default variant options created by Appipa that shouldn't be visible
 */
function hideDefaultCpcVariantOptions() {
  // Find all variant-option fieldsets
  const variantOptionFieldsets = document.querySelectorAll('.variant-option');
  
  variantOptionFieldsets.forEach(fieldset => {
    // Find all inputs within this fieldset
    const inputs = fieldset.querySelectorAll('input');
    
    inputs.forEach(input => {
      // Check if the input value starts with "Default_cpc"
      if (input.value && input.value.startsWith('Default_cpc')) {
        // Add the appipa-hidden class to visually hide the input
        input.classList.add('appipa-hidden');
        
        // Add data-appipa-persist to preserve during morphing
        input.setAttribute('data-appipa-persist', 'true');
        
        // Also hide the parent label if it exists (for radio button inputs)
        const label = input.closest('label');
        if (label) {
          label.classList.add('appipa-hidden');
          // Also add data-appipa-persist to the label to preserve it
          label.setAttribute('data-appipa-persist', 'true');
        }
      }
    });
  });
}

/**
 * Sets up a MutationObserver to watch for dynamically added variant options
 * that need to be hidden if they start with "Default_cpc"
 */
function setupDefaultCpcVariantObserver() {
  const observer = new MutationObserver((mutations) => {
    let shouldCheck = false;
    
    for (const mutation of mutations) {
      if (mutation.type === 'childList') {
        for (const addedNode of mutation.addedNodes) {
          if (addedNode instanceof HTMLElement) {
            // Check if a variant-option fieldset was added or if an input was added to one
            if (addedNode.classList.contains('variant-option') || 
                addedNode.closest('.variant-option') ||
                (addedNode.tagName === 'INPUT' && addedNode.closest('.variant-option'))) {
              shouldCheck = true;
              break;
            }
          }
        }
      }
    }
    
    if (shouldCheck) {
      // Use a small delay to ensure the DOM is fully updated
      setTimeout(() => {
        hideDefaultCpcVariantOptions();
      }, 50);
    }
  });
  
  // Observe the document body for changes
  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
  
  return observer;
}

async function loadCalculatorManager() {
  // Check if manager is already available globally
  if (window.AppipaCalculatorManager) {
    hideCalculator = window.AppipaCalculatorManager.hideCalculator;
    showCalculator = window.AppipaCalculatorManager.showCalculator;
    hideCalculatorAndRestoreDefaults = window.AppipaCalculatorManager.hideCalculatorAndRestoreDefaults;
    isCalculatorRendered = window.AppipaCalculatorManager.isCalculatorRendered;
    waitForAndHideCalculator = window.AppipaCalculatorManager.waitForAndHideCalculator;
    return true;
  }

  // Wait a bit for the script to load
  return new Promise((resolve) => {
    let attempts = 0;
    const checkInterval = setInterval(() => {
      attempts++;
      if (window.AppipaCalculatorManager) {
        hideCalculator = window.AppipaCalculatorManager.hideCalculator;
        showCalculator = window.AppipaCalculatorManager.showCalculator;
        hideCalculatorAndRestoreDefaults = window.AppipaCalculatorManager.hideCalculatorAndRestoreDefaults;
        isCalculatorRendered = window.AppipaCalculatorManager.isCalculatorRendered;
        waitForAndHideCalculator = window.AppipaCalculatorManager.waitForAndHideCalculator;
        clearInterval(checkInterval);
        resolve(true);
      } else if (attempts > 20) {
        // Give up after 2 seconds (20 * 100ms)
        clearInterval(checkInterval);
        resolve(false);
      }
    }, 100);
  });
}

class AppipaVariantIntegration {
  constructor() {
    this.hasCalculator = false;
    this.isCustomSizingSelected = false;
    this.variantPicker = null;
    this.defaultAddToCartButton = null;
    this.customSizingRadio = null;
    this.init();
  }

  async init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  async setup() {
    // Load calculator manager
    await loadCalculatorManager();

    // Find variant picker
    this.variantPicker = document.querySelector('variant-picker');
    if (!this.variantPicker) {
      return;
    }

    // Check if this is a custom product (product title contains "custom")
    this.isCustomProduct = this.variantPicker.hasAttribute('data-appipa-custom-product');

    // Wait for ProcessProductPage to render calculator, then hide it
    // Don't await this - let it run in background so we don't block
    this.waitForCalculator();

      // Listen for variant picker changes - use capture phase to intercept early
      this.variantPicker.addEventListener('change', (event) => {
        this.handleVariantChange(event);
      }, true); // Use capture phase to handle before variant picker processes it

      // When the Finish or Protective Coating option changes, toggle Custom
      // Sizing visibility.  Custom Sizing is only available when Finish is
      // "Mill Finish" OR Protective Coating is "Uncoated".
      this.variantPicker.addEventListener('change', (event) => {
        const target = /** @type {HTMLElement} */ (event.target);
        if (
          !target.closest('[data-option-name="finish"]') &&
          !target.closest('[data-option-name="protective coating"]')
        ) {
          return;
        }

        const customSizingLabel = this.variantPicker.querySelector(
          '[data-custom-sizing-label="true"]'
        );
        if (!customSizingLabel) return;

        const finishEligible = this.isFinishEligibleForCustomSizing();

        if (finishEligible) {
          // Only show if calculator is available
          const calculatorAvailable = this.hasCalculator ||
            this.variantPicker.hasAttribute('data-appipa-calculator-available');
          this.setCustomSizingVisibility(customSizingLabel, calculatorAvailable);
        } else {
          // If custom sizing was active and the user switched to a non-eligible
          // option, deactivate it
          if (this.isCustomSizingSelected) {
            this.deactivateCustomSizing();
          }
          this.setCustomSizingVisibility(customSizingLabel, false);
        }
      });

      // Listen for variant update events (fires after DOM morphing)
      // The custom sizing option should persist because it's in the template,
      // but we need to ensure it's visible if calculator is available
      const closestSection = this.variantPicker.closest('.shopify-section, dialog');
      if (closestSection) {
        closestSection.addEventListener('variant:update', (event) => {
          // If custom sizing is selected, prevent the variant update from resetting it
          if (this.isCustomSizingSelected && this.customSizingRadio) {
            // Restore custom sizing selection after variant update
            setTimeout(() => {
              if (this.customSizingRadio) {
                this.keepCustomSizingSelected(this.customSizingRadio);
              }
            }, 10);
          }
          
          // For custom products, ensure calculator stays visible
          if (this.isCustomProduct && this.hasCalculator) {
            setTimeout(() => {
              // Keep calculator visible and quantity selector hidden for custom products
              this.activateCustomSizing();
              const allSelectors = Array.from(document.querySelectorAll('.quantity-selector-wrapper'));
              allSelectors.forEach(selector => {
                selector.style.display = 'none';
                selector.setAttribute('data-appipa-hidden', 'true');
                selector.classList.add('appipa-quantity-hidden');
              });
            }, 50);
          } else if (this.hasCalculator || this.variantPicker?.hasAttribute('data-appipa-calculator-available')) {
            // Ensure custom sizing option is visible after morph if calculator is available
            this.hasCalculator = true; // Restore state in case it was lost
            setTimeout(() => {
              this.ensureCustomSizingOption();
              // Ensure variant ID is properly restored after morph
              if (!this.isCustomSizingSelected) {
                this.cleanupCalculatorFormFields();
              } else if (this.customSizingRadio) {
                // Re-apply custom sizing selection
                this.keepCustomSizingSelected(this.customSizingRadio);
                // Re-strip options[] names — morph re-creates elements with
                // their original name attributes which would cause
                // CANNOT_SET_NAME_FOR_LINKED_OPTION_VALUE on Combined Listings.
                this.stripOptionNameAttributes();
                // Re-hide quantity selector if custom sizing is active
                const allSelectors = Array.from(document.querySelectorAll('.quantity-selector-wrapper'));
                allSelectors.forEach(selector => {
                  selector.style.display = 'none';
                  selector.setAttribute('data-appipa-hidden', 'true');
                  selector.classList.add('appipa-quantity-hidden');
                });
              }
            }, 50); // Smaller delay since option should already be in DOM
          }
          
          // Re-apply appipa-hidden class to Default_cpc variant options after morphing
          // This ensures hidden variants persist after variant updates
          setTimeout(() => {
            hideDefaultCpcVariantOptions();
          }, 50);
        });
      }
      
      // Also listen on document as fallback
      document.addEventListener('variant:update', (event) => {
        // If custom sizing is selected, prevent the variant update from resetting it
        if (this.isCustomSizingSelected && this.customSizingRadio) {
          setTimeout(() => {
            if (this.customSizingRadio) {
              this.keepCustomSizingSelected(this.customSizingRadio);
            }
          }, 10);
        }
        
        // For custom products, ensure calculator stays visible
        if (this.isCustomProduct && this.hasCalculator) {
          setTimeout(() => {
            // Keep calculator visible and quantity selector hidden for custom products
            this.activateCustomSizing();
            const allSelectors = Array.from(document.querySelectorAll('.quantity-selector-wrapper'));
            allSelectors.forEach(selector => {
              selector.style.display = 'none';
              selector.setAttribute('data-appipa-hidden', 'true');
              selector.classList.add('appipa-quantity-hidden');
            });
          }, 50);
        } else if (this.hasCalculator || this.variantPicker?.hasAttribute('data-appipa-calculator-available')) {
          this.hasCalculator = true;
          setTimeout(() => {
            this.ensureCustomSizingOption();
            // Ensure variant ID is properly restored after morph
            if (!this.isCustomSizingSelected) {
              this.cleanupCalculatorFormFields();
            } else if (this.customSizingRadio) {
              // Re-apply custom sizing selection
              this.keepCustomSizingSelected(this.customSizingRadio);
              // Re-strip options[] names — morph re-creates elements with
              // their original name attributes which would cause
              // CANNOT_SET_NAME_FOR_LINKED_OPTION_VALUE on Combined Listings.
              this.stripOptionNameAttributes();
              // Re-hide quantity selector if custom sizing is active
              const allSelectors = Array.from(document.querySelectorAll('.quantity-selector-wrapper'));
              allSelectors.forEach(selector => {
                selector.style.display = 'none';
                selector.setAttribute('data-appipa-hidden', 'true');
                selector.classList.add('appipa-quantity-hidden');
              });
            }
          }, 50);
          
          // Re-apply appipa-hidden class to Default_cpc variant options after morphing
          // This ensures hidden variants persist after variant updates
          setTimeout(() => {
            hideDefaultCpcVariantOptions();
          }, 50);
        }
      });
  }

  async waitForCalculator() {
    // Check periodically for calculator (more lenient than timeout)
    if (waitForAndHideCalculator) {
      // Don't wait for the full timeout, check in background
      waitForAndHideCalculator().then(() => {
        const calculator = document.getElementById("calculator");
        if (calculator) {
          this.handleCalculatorDetected({ hasCalculator: true });
        }
      }).catch(() => {
        // Timeout is OK - just means no calculator, fallback to checking manually
        this.checkForCalculator();
      });
    } else {
      // Fallback: check periodically for calculator
      this.checkForCalculator();
    }
  }

  checkForCalculator() {
    // Check every 500ms for up to 10 seconds
    let attempts = 0;
    const maxAttempts = 20;
    
    const checkInterval = setInterval(() => {
      attempts++;
      const calculator = document.getElementById("calculator");
      
      if (calculator) {
        clearInterval(checkInterval);
        if (hideCalculator) {
          hideCalculator();
        }
        this.handleCalculatorDetected({ hasCalculator: true });
      } else if (attempts >= maxAttempts) {
        clearInterval(checkInterval);
      }
    }, 500);
  }

  handleCalculatorDetected(detail) {
    if (!detail.hasCalculator) {
      return;
    }

    this.hasCalculator = true;

    // Set data attribute on variant picker to indicate calculator is available
    // This helps with persistence through morphing
    if (this.variantPicker) {
      this.variantPicker.setAttribute('data-appipa-calculator-available', 'true');
    }

    // For custom products, always show the calculator and hide variant selector
    if (this.isCustomProduct) {
      // Show calculator immediately for custom products
      this.activateCustomSizing();
      
      // Hide quantity selector
      this.findQuantitySelector();
      if (this.quantitySelectorWrapper) {
        this.quantitySelectorWrapper.style.display = 'none';
        this.quantitySelectorWrapper.setAttribute('data-appipa-hidden', 'true');
        this.quantitySelectorWrapper.classList.add('appipa-quantity-hidden');
      }
      
      // Find and store references to form elements
      this.findAddToCartButton();
      this.findProductForm();
      
      // Enable calculator's event handler for custom products
      this.enableCalculatorEventHandler();
      
      return; // Skip normal custom sizing option logic for custom products
    }

    // Show the custom sizing radio button (for non-custom products)
    this.showCustomSizingOption();

    // Find and store references to form elements
    this.findAddToCartButton();
    this.findProductForm();
    this.findQuantitySelector();

    // Initially disable calculator's event handler since custom sizing is not selected
    this.disableCalculatorEventHandler();
    
    // Also clean up calculator form fields immediately on initial load
    // This ensures the form is in a clean state for regular variant submissions
    this.cleanupCalculatorFormFields();
  }

  findProductForm() {
    // Find the product form
    this.productForm = document.querySelector('form[action*="/cart/add"]');
  }

  cleanupCalculatorFormFields() {
    // Clean up calculator form fields so they don't interfere with regular variant submissions
    // This should be called on initial load and when deactivating custom sizing
    
    // Disable all calculator inputs so they don't get submitted
    const calculator = document.getElementById("calculator");
    if (calculator) {
      const calculatorInputs = calculator.querySelectorAll('input, select, textarea');
      calculatorInputs.forEach(input => {
        input.disabled = true;
        input.setAttribute('data-appipa-disabled', 'true');
      });
    }

    // Disable calculator-specific hidden inputs
    const calculatorHiddenInputs = document.querySelectorAll(
      '#variantIdCustom, #cpcPriceToken, #calculation_inp_Price_Each_Inc_VAT'
    );
    calculatorHiddenInputs.forEach(input => {
      input.disabled = true;
      input.setAttribute('data-appipa-disabled', 'true');
    });

    // Restore variant ID field from oldId to id
    // IMPORTANT: Must preserve the ref="variantId" attribute for ProductFormComponent
    const productForm = this.productForm || document.querySelector('form[action*="/cart/add"]');
    if (!productForm) {
      return;
    }

    // Find the ProductFormComponent to get the variant ID from its refs
    const productFormComponent = productForm.closest('product-form-component') || 
                                  productForm.querySelector('product-form-component') ||
                                  document.querySelector('product-form-component');
    
    // Try to get variant ID from ProductFormComponent refs first
    let targetVariantId = null;
    if (productFormComponent && productFormComponent.refs && productFormComponent.refs.variantId) {
      const refVariantId = productFormComponent.refs.variantId;
      targetVariantId = refVariantId.value;
      
      // Ensure it has the correct name and is enabled
      refVariantId.name = 'id';
      refVariantId.disabled = false;
      if (!refVariantId.hasAttribute('ref')) {
        refVariantId.setAttribute('ref', 'variantId');
      }
    }

    // Find the oldId input (created by ProcessProductPage)
    const oldIdInput = productForm.querySelector('input[name="oldId"], select[name="oldId"]');
    
    // Find the variant ID input with ref="variantId"
    let variantIdInput = productForm.querySelector('input[ref="variantId"]');
    
    if (!variantIdInput) {
      // If no variant ID input exists, create one or restore from oldId
      if (oldIdInput) {
        oldIdInput.setAttribute("name", "id");
        oldIdInput.setAttribute("ref", "variantId");
        oldIdInput.disabled = false;
        variantIdInput = oldIdInput;
        targetVariantId = targetVariantId || oldIdInput.value;
      } else {
        // Create a new variant ID input if it doesn't exist
        variantIdInput = document.createElement('input');
        variantIdInput.type = 'hidden';
        variantIdInput.name = 'id';
        variantIdInput.setAttribute('ref', 'variantId');
        variantIdInput.value = targetVariantId || '';
        
        // Insert it at the beginning of the form
        productForm.insertBefore(variantIdInput, productForm.firstChild);
      }
    } else {
      // Update existing variant ID input
      if (oldIdInput && oldIdInput.value) {
        variantIdInput.value = oldIdInput.value;
        targetVariantId = oldIdInput.value;
        oldIdInput.remove();
      }
      variantIdInput.name = 'id';
      variantIdInput.disabled = false;
      if (!variantIdInput.hasAttribute('ref')) {
        variantIdInput.setAttribute('ref', 'variantId');
      }
    }

    // Set the value if we have a target variant ID
    if (targetVariantId && variantIdInput.value !== targetVariantId) {
      variantIdInput.value = targetVariantId;
    }

    // Remove or disable any incorrect "id" inputs that aren't the main variant ID
    const allIdInputs = productForm.querySelectorAll('input[name="id"]');
    allIdInputs.forEach((input) => {
      // Skip the main variant ID input
      if (input === variantIdInput || (input.hasAttribute('ref') && input.getAttribute('ref') === 'variantId')) {
        return;
      }
      
      if (input.id === 'variantIdCustom') {
        // Disable the calculator's variant ID input
        input.disabled = true;
        input.setAttribute('data-appipa-disabled', 'true');
      } else {
        // Remove any other "id" inputs (they're duplicates or incorrect)
        input.remove();
      }
    });
  }

  showCustomSizingOption() {
    this.ensureCustomSizingOption();
  }

  /**
   * Applies an accessible visible/hidden state for the Custom Sizing CTA.
   * Hidden state removes it from visual layout, accessibility tree, and keyboard flow.
   * @param {HTMLElement | null} customSizingLabel
   * @param {boolean} isVisible
   */
  setCustomSizingVisibility(customSizingLabel, isVisible) {
    if (!customSizingLabel) return;

    const customSizingRadio = customSizingLabel.querySelector('[data-custom-sizing="true"]');

    if (isVisible) {
      customSizingLabel.style.display = '';
      customSizingLabel.hidden = false;
      customSizingLabel.removeAttribute('aria-hidden');
      customSizingLabel.removeAttribute('inert');

      if (customSizingRadio) {
        customSizingRadio.disabled = false;
        customSizingRadio.removeAttribute('tabindex');
      }
      return;
    }

    customSizingLabel.style.display = 'none';
    customSizingLabel.hidden = true;
    customSizingLabel.setAttribute('aria-hidden', 'true');
    customSizingLabel.setAttribute('inert', '');

    if (customSizingRadio) {
      customSizingRadio.checked = false;
      customSizingRadio.dataset.currentChecked = 'false';
      customSizingRadio.disabled = true;
      customSizingRadio.setAttribute('tabindex', '-1');
    }
  }

  /**
   * Returns the fieldset that should host the Custom Sizing button.
   * Priority: "length" fieldset → "finish" fieldset.
   */
  getCustomSizingFieldset() {
    if (!this.variantPicker) return null;

    // Prefer the "Length" option set
    const lengthFieldset = this.variantPicker.querySelector(
      'fieldset[data-option-name="length"]'
    );
    if (lengthFieldset) return { fieldset: lengthFieldset, context: 'length' };

    // Fall back to "Finish"
    const finishFieldset = this.variantPicker.querySelector(
      'fieldset[data-option-name="finish"]'
    );
    if (finishFieldset) return { fieldset: finishFieldset, context: 'finish' };

    return null;
  }

  /**
   * Returns the label text for the Custom Sizing button based on context.
   * @param {'length' | 'finish'} context
   */
  getCustomSizingLabelText(context) {
    return context === 'finish' ? 'Mill Finish - Custom Size' : 'Custom Sizing';
  }

  /**
   * Returns true when the currently selected options allow Custom Sizing.
   *
   * Eligible when:
   *  • The "Finish" option is "Mill Finish", OR
   *  • The "Protective Coating" option is "Uncoated".
   *
   * Uses both `data-current-checked` and `:checked` to cope with post-morph
   * states where one might be stale.
   */
  isFinishEligibleForCustomSizing() {
    if (!this.variantPicker) return false;

    // Helper — reads the active value from a fieldset (radio or dropdown)
    const getSelectedValue = (fieldset) => {
      if (!fieldset) return null;
      
      // Radio-button style
      const checkedRadio =
        fieldset.querySelector('input[type="radio"][data-current-checked="true"]') ||
        fieldset.querySelector('input[type="radio"]:checked');
      if (checkedRadio) {
        // Ignore the Custom Sizing radio — not a real option value
        if (checkedRadio.dataset.customSizing === 'true') return null;
        return checkedRadio.value.trim().toLowerCase();
      }

      // Dropdown style
      const select = fieldset.querySelector('select');
      if (select) return select.value.trim().toLowerCase();

      return null;
    };

    const finishFieldset = this.variantPicker.querySelector('[data-option-name="finish"]');
    const coatingFieldset = this.variantPicker.querySelector('[data-option-name="protective coating"]');
    const lengthFieldset = this.variantPicker.querySelector('[data-option-name="length"]');
    const coatingValue = getSelectedValue(coatingFieldset);
    const finishValue = getSelectedValue(finishFieldset);

    // Edge case: some products only expose a Length option set.
    // In that scenario there is no Finish/Coating gate to evaluate, so keep
    // Custom Sizing eligible when a calculator is available.
    if (!finishFieldset && !coatingFieldset && !!lengthFieldset) return true;

    // Eligible when either condition is met.
    if (coatingValue === 'uncoated' || finishValue === 'mill finish') return true;

    return false;
  }

  ensureCustomSizingOption() {
    // Check if calculator is available (either from state or data attribute)
    const calculatorAvailable = this.hasCalculator || 
                                (this.variantPicker && this.variantPicker.hasAttribute('data-appipa-calculator-available'));
    
    if (!calculatorAvailable) {
      // Hide custom sizing if calculator is not available
      const customSizingLabel = this.variantPicker?.querySelector('[data-custom-sizing-label="true"]');
      if (customSizingLabel) {
        if (this.isCustomSizingSelected) {
          this.deactivateCustomSizing();
        }
        this.setCustomSizingVisibility(customSizingLabel, false);
      }
      return;
    }

    // Determine which fieldset should host the button
    const target = this.getCustomSizingFieldset();
    if (!target) return;

    const { fieldset, context } = target;

    // Find the custom sizing label (should exist in template)
    let customSizingLabel = this.variantPicker.querySelector(
      '[data-custom-sizing-label="true"]'
    );
    
    // If not found after morphing, it may have been removed - re-inject it
    if (!customSizingLabel) {
      if (fieldset.classList.contains('variant-option--buttons')) {
        // Re-inject the custom sizing option (fallback)
        this.injectCustomSizingOption(fieldset, context);
        customSizingLabel = this.variantPicker.querySelector(
          '[data-custom-sizing-label="true"]'
        );
      }
    }
    
    if (customSizingLabel) {
      // Only show if the currently selected finish allows custom sizing
      const finishEligible = this.isFinishEligibleForCustomSizing();
      this.setCustomSizingVisibility(customSizingLabel, finishEligible);

      this.customSizingRadio = customSizingLabel.querySelector('[data-custom-sizing="true"]');
      
      // Re-attach change listener if needed
      if (this.customSizingRadio && !this.customSizingRadio._appipaListenerAttached) {
        const changeHandler = (event) => {
          this.handleVariantChange(event);
        };
        this.customSizingRadio.addEventListener('change', changeHandler);
        this.customSizingRadio._appipaListenerAttached = true;
      }
    }
  }

  /**
   * Dynamically creates and injects the Custom Sizing button into a fieldset.
   * @param {HTMLElement} fieldset
   * @param {'length' | 'finish'} context
   */
  injectCustomSizingOption(fieldset, context = 'length') {
    // Get the product option name and block ID from existing radio buttons
    const existingRadio = fieldset.querySelector('input[type="radio"]');
    if (!existingRadio) return;

    const radioName = existingRadio.getAttribute('name');
    const fieldsetIndex = parseInt(existingRadio.dataset.fieldsetIndex || '0');
    const lastInputIndex = fieldset.querySelectorAll('input[type="radio"]').length;
    const labelText = this.getCustomSizingLabelText(context);

    // Create the custom sizing label element
    const customSizingLabel = document.createElement('label');
    customSizingLabel.className = 'variant-option__button-label variant-option__button-label--custom-sizing';
    customSizingLabel.setAttribute('data-custom-sizing-label', 'true');
    customSizingLabel.hidden = true;
    customSizingLabel.setAttribute('aria-hidden', 'true');
    customSizingLabel.setAttribute('inert', '');
    
    const radio = document.createElement('input');
    radio.type = 'radio';
    radio.name = radioName;
    radio.value = 'custom-sizing';
    radio.setAttribute('aria-label', labelText);
    radio.dataset.previousChecked = 'false';
    radio.dataset.fieldsetIndex = fieldsetIndex.toString();
    radio.dataset.inputIndex = lastInputIndex.toString();
    radio.dataset.inputId = `${fieldsetIndex}-${lastInputIndex}`;
    radio.dataset.optionValueId = 'custom-sizing';
    radio.dataset.optionAvailable = 'true';
    radio.dataset.customSizing = 'true';
    radio.dataset.currentChecked = 'false';
    radio.disabled = true;
    radio.setAttribute('tabindex', '-1');

    const pill = document.createElement('span');
    pill.className = 'variant-option__button-label__pill';

    const text = document.createElement('span');
    text.className = 'variant-option__button-label__text';
    text.textContent = labelText;

    customSizingLabel.appendChild(radio);
    customSizingLabel.appendChild(pill);
    customSizingLabel.appendChild(text);

    // Append to fieldset
    fieldset.appendChild(customSizingLabel);
    
    // Re-attach change listener
    const changeHandler = (event) => {
      this.handleVariantChange(event);
    };
    radio.addEventListener('change', changeHandler);
    radio._appipaListenerAttached = true;
  }

  findAddToCartButton() {
    // Find the default add-to-cart button
    // It could be in various locations depending on theme structure
    this.defaultAddToCartButton =
      document.querySelector('button[type="submit"][name="add"]') ||
      document.querySelector('add-to-cart-component button') ||
      document.querySelector('.add-to-cart-button') ||
      document.querySelector('[ref="addToCartButton"]');
  }

  findQuantitySelector() {
    // Find the quantity selector wrapper within the product form context
    // Try multiple strategies to find it
    let selector = null;
    
    // Strategy 1: Find within product form
    if (this.productForm) {
      selector = this.productForm.querySelector('.quantity-selector-wrapper');
    }
    
    // Strategy 2: Find by ref attribute
    if (!selector) {
      selector = document.querySelector('[ref="quantitySelectorWrapper"]');
    }
    
    // Strategy 3: Find by class in product form buttons
    if (!selector) {
      const productFormButtons = document.querySelector('.product-form-buttons');
      if (productFormButtons) {
        selector = productFormButtons.querySelector('.quantity-selector-wrapper');
      }
    }
    
    // Strategy 4: Find anywhere in document (last resort)
    if (!selector) {
      selector = document.querySelector('.quantity-selector-wrapper');
    }
    
    this.quantitySelectorWrapper = selector;
  }

  handleVariantChange(event) {
    // Skip variant change handling for custom products (variant selector is hidden)
    if (this.isCustomProduct) {
      return;
    }
    
    if (!this.hasCalculator) {
      return; // No calculator available, do nothing
    }

    const target = event.target;
    
    // Check if the changed input is the custom sizing radio
    const isCustomSizing = target.dataset.customSizing === 'true' && target.checked;

    if (isCustomSizing) {
      // Prevent variant picker from processing this as a normal variant change
      // We need to stop propagation early so variant picker doesn't process it
      event.stopImmediatePropagation();
      event.preventDefault();
      event.stopPropagation();
      
      // Store the selection immediately to prevent it from being reset
      const fieldset = target.closest('fieldset');
      if (fieldset) {
        // Mark fieldset to prevent variant picker from resetting
        fieldset.setAttribute('data-appipa-custom-sizing-fieldset', 'true');
      }
      
      // Update variant picker's internal state manually to keep visual selection
      // Do this synchronously to ensure it happens before any async operations
      this.keepCustomSizingSelected(target);
      
      // Activate calculator after a small delay to ensure visual state is set
      setTimeout(() => {
        this.activateCustomSizing();
      }, 0);
      
      return false; // Prevent any further processing
    } else if (target.checked && !target.dataset.customSizing) {
      // Any other variant option was selected
      // Remove the marker from fieldset
      const fieldset = target.closest('fieldset');
      if (fieldset) {
        fieldset.removeAttribute('data-appipa-custom-sizing-fieldset');
      }
      
      this.deactivateCustomSizing();
    }
  }

  keepCustomSizingSelected(radioInput) {
    // Update the variant picker's internal state to keep custom sizing selected
    if (!radioInput) return;
    
    const fieldset = radioInput.closest('fieldset');
    if (!fieldset) return;

    const fieldsetIndex = parseInt(radioInput.dataset.fieldsetIndex || '0');
    const inputIndex = parseInt(radioInput.dataset.inputIndex || '0');
    
    // Use variant picker's updateSelectedOption method if available
    if (this.variantPicker && typeof this.variantPicker.updateSelectedOption === 'function') {
      try {
        this.variantPicker.updateSelectedOption(radioInput);
      } catch (e) {
        // Fallback to manual update if variant picker rejects it
        this.manualUpdateCustomSizingState(radioInput, fieldset, fieldsetIndex, inputIndex);
      }
    } else {
      // Manual update if variant picker method not available
      this.manualUpdateCustomSizingState(radioInput, fieldset, fieldsetIndex, inputIndex);
    }
  }

  manualUpdateCustomSizingState(radioInput, fieldset, fieldsetIndex, inputIndex) {
    // Manually update the visual state to match variant picker's expected format
    const allRadios = Array.from(fieldset.querySelectorAll('input[type="radio"]'));
    
    // Find the previously checked radio (excluding custom sizing)
    const previousChecked = allRadios.find(radio => 
      radio.dataset.currentChecked === 'true' && 
      radio !== radioInput &&
      radio.dataset.customSizing !== 'true'
    );
    
    // Update previous radio state
    if (previousChecked) {
      previousChecked.dataset.previousChecked = 'true';
      previousChecked.dataset.currentChecked = 'false';
      previousChecked.checked = false;
      
      const prevLabel = previousChecked.closest('label');
      if (prevLabel) {
        fieldset.style.setProperty(
          '--pill-width-previous',
          `${prevLabel.offsetWidth || 0}px`
        );
      }
    }
    
    // Clear any other current checked states
    allRadios.forEach(radio => {
      if (radio !== radioInput && radio.dataset.currentChecked === 'true') {
        radio.dataset.currentChecked = 'false';
        radio.checked = false;
      }
    });
    
    // Set custom sizing as checked
    radioInput.checked = true;
    radioInput.dataset.currentChecked = 'true';
    radioInput.dataset.previousChecked = 'false';
    
    // Update the pill visual state
    const label = radioInput.closest('label');
    if (label) {
      const labelWidth = label.offsetWidth || label.getBoundingClientRect().width || 0;
      fieldset.style.setProperty('--pill-width-current', `${labelWidth}px`);
      
      // Force a reflow to ensure the style update is applied
      void fieldset.offsetHeight;
    }
    
    // Continuously check and maintain the selection - the variant picker might try to reset it
    let checkInterval;
    const maxChecks = 10;
    let checkCount = 0;
    
    const maintainSelection = () => {
      if (checkCount >= maxChecks) {
        clearInterval(checkInterval);
        return;
      }
      
      if (radioInput && !radioInput.checked && this.isCustomSizingSelected) {
        // Restore the selection if it was reset
        radioInput.checked = true;
        radioInput.dataset.currentChecked = 'true';
        if (label) {
          const labelWidth = label.offsetWidth || label.getBoundingClientRect().width || 0;
          fieldset.style.setProperty('--pill-width-current', `${labelWidth}px`);
        }
      }
      
      checkCount++;
    };
    
    // Check every 50ms for the first 500ms
    checkInterval = setInterval(maintainSelection, 50);
    
    // Also check after a longer delay to catch any late resets
    setTimeout(() => {
      clearInterval(checkInterval);
      if (this.isCustomSizingSelected && radioInput && !radioInput.checked) {
        this.keepCustomSizingSelected(radioInput);
      }
    }, 300);
  }

  disableCalculatorEventHandler() {
    // Remove the calculator's event handler from the submit button
    // This prevents it from intercepting regular variant submissions
    if (window.cpc && window.cpc.submitButton && window.cpc.addToCartHandler) {
      const submitButton = window.cpc.submitButton;
      const handler = window.cpc.addToCartHandler;
      
      // Remove the calculator's handler
      submitButton.removeEventListener('click', handler);
      submitButton.removeEventListener('submit', handler);
    }

    // Remove the cpc-calculator-form class so ProcessProductPage doesn't prevent form submission
    if (this.productForm) {
      this.productForm.classList.remove('cpc-calculator-form');
    }
  }

  enableCalculatorEventHandler() {
    // Re-add the cpc-calculator-form class so calculator can handle form submission
    if (this.productForm) {
      this.productForm.classList.add('cpc-calculator-form');
    }

    // Re-enable the calculator's event handler
    if (window.cpc && window.cpc.submitButton && window.cpc.addToCartHandler) {
      const submitButton = window.cpc.submitButton;
      const handler = window.cpc.addToCartHandler;
      
      // Re-add the calculator's handler
      submitButton.addEventListener('click', handler);
    }
  }

  /**
   * Strips the `name` attribute from all `options[...]`-named elements on the
   * page (selects and inputs).  The original name is stored in
   * `data-original-option-name` so it can be restored later.
   *
   * This prevents Appipa's ProcessProductPage from serializing these values
   * into its cart-add XHR.  Shopify rejects `options[]` parameters for
   * Combined Listings products with linked option values
   * (CANNOT_SET_NAME_FOR_LINKED_OPTION_VALUE).
   */
  stripOptionNameAttributes() {
    const optionElements = document.querySelectorAll(
      'select[name^="options["], input[name^="options["]'
    );
    optionElements.forEach((el) => {
      if (!el.hasAttribute('data-original-option-name')) {
        el.setAttribute('data-original-option-name', el.getAttribute('name'));
      }
      el.removeAttribute('name');
    });
  }

  /**
   * Restores `name` attributes that were previously stripped by
   * `stripOptionNameAttributes()`.
   */
  restoreOptionNameAttributes() {
    const strippedElements = document.querySelectorAll('[data-original-option-name]');
    strippedElements.forEach((el) => {
      el.setAttribute('name', el.getAttribute('data-original-option-name'));
      el.removeAttribute('data-original-option-name');
    });
  }

  async activateCustomSizing() {
    // Always strip options[] name attributes when the calculator is (or is
    // about to be) active.  DOM morphs / variant:update events re-create
    // elements with their original `name` attributes, so we must re-strip
    // on *every* call — including the early-return path below.
    // Shopify rejects options[] for Combined Listings products with linked
    // option values (CANNOT_SET_NAME_FOR_LINKED_OPTION_VALUE).
    this.stripOptionNameAttributes();

    if (this.isCustomSizingSelected) {
      return; // Already active
    }

    this.isCustomSizingSelected = true;

    // Ensure custom sizing radio stays visually selected
    if (this.customSizingRadio) {
      this.keepCustomSizingSelected(this.customSizingRadio);
    }

    // Hide quantity selector - find all instances and hide them
    this.findQuantitySelector();
    
    // Also find all quantity selector wrappers in the document (there might be multiple)
    const allQuantitySelectors = [
      this.quantitySelectorWrapper,
      ...Array.from(document.querySelectorAll('.quantity-selector-wrapper'))
    ].filter(Boolean); // Remove null/undefined
    
    // Remove duplicates
    const uniqueSelectors = [...new Set(allQuantitySelectors)];
    
    if (uniqueSelectors.length > 0) {
      uniqueSelectors.forEach(selector => {
        selector.style.display = 'none';
        selector.setAttribute('data-appipa-hidden', 'true');
        selector.classList.add('appipa-quantity-hidden');
      });
    } else {
      // Try again after a small delay in case it hasn't rendered yet
      setTimeout(() => {
        const delayedSelectors = Array.from(document.querySelectorAll('.quantity-selector-wrapper'));
        if (delayedSelectors.length > 0) {
          delayedSelectors.forEach(selector => {
            selector.style.display = 'none';
            selector.setAttribute('data-appipa-hidden', 'true');
            selector.classList.add('appipa-quantity-hidden');
          });
        }
      }, 100);
    }

    // Enable calculator's event handler
    this.enableCalculatorEventHandler();

    // Show the calculator (it was already rendered by ProcessProductPage, just hidden)
    // Also transform number inputs to match quantity selector styling
    if (showCalculator) {
      showCalculator();
      // Ensure number inputs are transformed (showCalculator handles this, but double-check)
      if (window.AppipaCalculatorManager && window.AppipaCalculatorManager.transformCalculatorNumberInputs) {
        setTimeout(() => {
          window.AppipaCalculatorManager.transformCalculatorNumberInputs();
        }, 50);
      }
      this.isCustomSizingSelected = true;
    } else {
      // Fallback: Show calculator manually
      const calculator = document.getElementById("calculator");
      const container = document.querySelector('.cpc-calculator-target') || 
                        document.getElementById('calculator-block');
      
      if (calculator) {
        calculator.style.display = '';
        calculator.classList.remove('appipa-calculator-hidden');
      }
      
      if (container) {
        container.style.display = '';
        container.classList.remove('appipa-calculator-hidden');
      }
      
      this.isCustomSizingSelected = true;
    }

    // Set a flag on the variant picker to prevent it from resetting
    if (this.variantPicker) {
      this.variantPicker.setAttribute('data-appipa-custom-sizing-active', 'true');
    }
  }

  deactivateCustomSizing() {
    if (!this.isCustomSizingSelected) {
      return; // Already deactivated
    }

    // Disable calculator's event handler - don't let it intercept regular submissions
    this.disableCalculatorEventHandler();

    // Hide the calculator and restore defaults
    if (hideCalculatorAndRestoreDefaults) {
      hideCalculatorAndRestoreDefaults();
    } else {
      // Fallback: Hide calculator manually
      if (hideCalculator) {
        hideCalculator();
      } else {
        const calculator = document.getElementById("calculator");
        const container = document.querySelector('.cpc-calculator-target') || 
                          document.getElementById('calculator-block');
        
        if (calculator) {
          calculator.style.display = 'none';
          calculator.classList.add('appipa-calculator-hidden');
        }
        
        if (container) {
          container.style.display = 'none';
          container.classList.add('appipa-calculator-hidden');
        }
      }
    }

    // Show quantity selector - find all instances that we hid and show them
    this.findQuantitySelector();
    
    // Find all quantity selectors that we previously hid
    const hiddenSelectors = Array.from(document.querySelectorAll('[data-appipa-hidden="true"].quantity-selector-wrapper, .appipa-quantity-hidden'));
    
    if (hiddenSelectors.length > 0) {
      hiddenSelectors.forEach(selector => {
        selector.style.display = '';
        selector.removeAttribute('data-appipa-hidden');
        selector.classList.remove('appipa-quantity-hidden');
      });
    } else if (this.quantitySelectorWrapper) {
      // Fallback: show the one we found
      this.quantitySelectorWrapper.style.display = '';
      this.quantitySelectorWrapper.removeAttribute('data-appipa-hidden');
      this.quantitySelectorWrapper.classList.remove('appipa-quantity-hidden');
    }

    // Restore options[] name attributes that were stripped during activation
    this.restoreOptionNameAttributes();

    // Clean up calculator form fields
    this.cleanupCalculatorFormFields();

    this.isCustomSizingSelected = false;
    
    // Remove the flag
    if (this.variantPicker) {
      this.variantPicker.removeAttribute('data-appipa-custom-sizing-active');
    }
  }
}

// Initialize integration when script loads
const appipaIntegration = new AppipaVariantIntegration();

// Hide default CPC variant options on page load and set up observer
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    hideDefaultCpcVariantOptions();
    setupDefaultCpcVariantObserver();
  });
} else {
  hideDefaultCpcVariantOptions();
  setupDefaultCpcVariantObserver();
}

export default AppipaVariantIntegration;

